// src/features/task/TaskWorkRailPanel.tsx
// The task page's working parts — checklists, time, dependencies, attachments —
// living in the right rail's Work tab. They moved off the detail page's add-bar
// (which only revealed empty sections and whose adds did not land); here every
// section is mounted for real, so its own "+ add" affordance works.
//
// Fetches the task fresh on mount: list pages fill the store cache with rows
// that lack the detail-only parts (checklists), so invalidate first.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useTask } from '@react/hooks';
import { Alert } from '@react/components';
import { tabsStore } from '@/stores/TabsStore';
import { TaskChecklists } from '@/features/task/TaskChecklists';
import { TaskTimeTracking } from '@/features/task/TaskTimeTracking';
import { TaskAttachments } from '@/features/task/TaskAttachments';
import { errMsg } from '@/features/task/panelHelpers';
import type { EntityPanelProps } from '@/features/layout/entityPanels';

export const TaskWorkRailPanel = observer(({ entity }: EntityPanelProps) => {
  const { t } = useI18n();
  const store = useTask();
  const taskId = entity.id;

  const [task, setTask] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      store.invalidateTask(taskId);
      const res: any = await store.GetTask(taskId);
      setTask(res?.task ?? res);
    } catch (e) {
      setError(errMsg(e, t('task.workRail.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => { void load(); }, [load]);

  if (loading && !task) {
    return (
      <div className="space-y-4" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2 border-t border-border pt-3 first:border-t-0 first:pt-0">
            <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-8 animate-pulse rounded-md bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (error && !task) {
    return (
      <Alert variant="error">
        <div className="flex items-center gap-2">
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="shrink-0 rounded px-2 py-0.5 text-xs underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('chat.entityPanel.retry')}
          </button>
        </div>
      </Alert>
    );
  }

  // Normalize: detail responses wrap the list ({checklists: [...]}), and the
  // rail must never hand a section a non-array where it maps/filters.
  const rawChecklists = task?.checklists?.checklists ?? task?.checklists;
  const checklists = Array.isArray(rawChecklists) ? rawChecklists : [];

  return (
    // No TaskSectionsProvider: outside one, every section draws itself with
    // its own working add affordance — exactly what the rail is for.
    <div className="space-y-4 [&_section]:pt-3">
      <TaskChecklists
        taskId={taskId}
        checklists={checklists}
        onChanged={load}
      />
      <TaskTimeTracking taskId={taskId} />
      <TaskAttachments tabId={tabsStore.activeTabId} />
    </div>
  );
});

export default TaskWorkRailPanel;
